import dayjs, { ConfigType } from "dayjs";
import { catching } from "shared/utils/fns";
import { z } from "zod";

export const nonemptyString = () => z.string().trim().min(1, "This field is required");
export const nonemptyUUID = () => z.string().uuid("This field is required");
export const coercedUUID = () =>
    z.any().transform((val, ctx) => {
        const maybeUUID = typeof val === "string" ? val : val && typeof val === "object" && typeof val.id === "string" ? val.id : null;

        try {
            return z.string().uuid().parse(maybeUUID);
        } catch {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: "Expected a UUID or an object with { id: UUID }",
            });
            return z.NEVER;
        }
    });

export const zDate = z.preprocess(
    (it) => (typeof it == "string" ? +it || it : it),
    z.preprocess((it) => catching(() => dayjs(it as ConfigType)) ?? it, z.coerce.date()),
);
export const zInstant = z
    .preprocess((it) => catching(() => dayjs(it as ConfigType)) ?? it, z.coerce.date())
    .transform((it) => +it)
    .transform((it) => it.toString());

/**
 * Zod bindings for GeoJSON
 */
export const geozod = {
    position: () => z.number().array().min(2).max(3) as z.ZodType<GeoJSON.Position>,
    point: () => z.object({ type: z.literal("Point"), coordinates: geozod.position() }) satisfies z.ZodType<GeoJSON.Point>,
    polygon: () =>
        z.object({
            type: z.literal("Polygon"),
            coordinates: geozod.position().array().array(),
        }) satisfies z.ZodType<GeoJSON.Polygon>,
    multipolygon: () =>
        z.object({
            type: z.literal("MultiPolygon"),
            coordinates: geozod.position().array().array().array(),
        }) satisfies z.ZodType<GeoJSON.MultiPolygon>,
    feature: <G extends GeoJSON.Geometry>(geometry: z.ZodType<G>) =>
        z.object({
            id: z.coerce.string(), // per the official RFC-7946, this 'id' is optional but for our use cases we want to keep it required
            type: z.literal("Feature"),
            properties: z.object({}).passthrough().default({}),
            geometry,
        }) as z.ZodType<GeoJSON.Feature<G>>,
};
